import { toast } from 'react-toastify';
// For reasons we can't figure out, eslint can't handle these modules. TS can compile it fine.
import * as WebDevices from 'web-device-mux';
import * as WebReceipt from 'web-receiptline-printer';
import * as PrinterBase from './PrinterBase';

type PrinterManager = WebDevices.UsbDeviceManager<WebReceipt.ReceiptPrinterUsb>;
type PrinterManagerEventMap = WebDevices.IUsbDeviceManagerEventMap<WebReceipt.ReceiptPrinterUsb>;

interface ReceiptPrinterConfig extends PrinterBase.PrinterConfig {
  serial?: string;
  width?: number;
}

interface ReceiptPrinterInfo extends PrinterBase.PrinterInfoBase {
  serial: string;
  config: ReceiptPrinterConfig;
}

export class ReceiptPrinterWeb implements PrinterBase.ReceiptPrinterBase {
  public serial = '';
  public readonly canConfigure = false;
  public readonly type = PrinterBase.PrinterType.WebReceipt;
  public readonly uid;

  public constructor(
    public readonly name: string,
    private readonly printer: WebReceipt.ReceiptPrinterUsb,
  ) {
    this.serial = printer.printerOptions.serialNumber;
    this.uid = PrinterBase.getPrinterUid(this.type, this.serial);
  }

  public async print(receiptline: string): Promise<void> {
    const doc = WebReceipt.parseReceiptLineToDocument(receiptline, this.printer.printerOptions);

    await this.printer.sendDocument(doc);
  }

  public async testPrint(): Promise<void> {
    await this.printer.sendDocument({
      commands: [new WebReceipt.TestPrint('rolling')],
    });
  }

  public async drawerKick(): Promise<void> {
    await this.printer.sendDocument({
      commands: [new WebReceipt.PulseCommand()],
    });
  }
}

export class ReceiptPrinterManager {
  public name = 'Receipt Printer with WebUSB';
  public type = PrinterBase.PrinterType.WebReceipt;
  public supportsManualConnect = true;
  public status: PrinterBase.PrinterManagerStatus = PrinterBase.PrinterManagerStatus.Scanning;

  private readonly mgr?: PrinterManager;

  public constructor() {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!window.navigator.usb) {
      // TODO: Better handling for non-WebUSB browsers
      this.mgr = undefined;
      this.status = PrinterBase.PrinterManagerStatus.Error;
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (window.receiptPrinterManager) {
      this.mgr = window.receiptPrinterManager;
      return;
    }

    this.mgr = new WebDevices.UsbDeviceManager(
      window.navigator.usb,
      async (device, options) => await WebReceipt.ReceiptPrinter.fromUSBDevice(device, options),
      {
        requestOptions: {
          filters: [
            {
              // Epson
              vendorId: 0x04_b8,
            },
          ],
        },
        /*
         * Print debug messages to the console
         * TODO: This is very chatty!
         */
        debug: true,
      },
    );

    this.mgr
      .forceReconnect()
      .then(() => {
        this.status = PrinterBase.PrinterManagerStatus.Ready;
      })
      .catch((error: Error) => {
        // TODO: No translation context available
        toast.error(error.message);
        this.status = PrinterBase.PrinterManagerStatus.Error;
      });

    window.receiptPrinterManager = this.mgr;
  }

  public addEventListener<T extends keyof PrinterManagerEventMap>(
    type: T,
    listener:
      | EventListenerObject
      | ((this: PrinterManager, ev: PrinterManagerEventMap[T]) => void)
      | null,
    options?: AddEventListenerOptions | boolean,
  ): void {
    this.mgr?.addEventListener(type, listener, options);
  }

  public removeEventListener<T extends keyof PrinterManagerEventMap>(
    type: T,
    listener:
      | EventListenerObject
      | ((this: PrinterManager, ev: PrinterManagerEventMap[T]) => void)
      | null,
    options?: AddEventListenerOptions | boolean,
  ): void {
    this.mgr?.removeEventListener(type, listener, options);
  }

  public async startManualConnect?(): Promise<void> {
    await this.mgr?.promptForNewDevice();
  }

  public getAvailablePrinters(): ReceiptPrinterInfo[] {
    return this.mgr?.devices.map((d) => this.toPrinterInfo(d)) ?? [];
  }

  public getPrinters(): ReceiptPrinterWeb[] {
    return (
      this.mgr?.devices.map((d) => new ReceiptPrinterWeb(this.getPrinterName(d, true), d)) ?? []
    );
  }

  public usePrinter(serial: string): PrinterBase.ReceiptPrinterBase | undefined {
    const printer = this.mgr?.devices.find((d) => d.printerOptions.serialNumber === serial);
    if (printer !== undefined && this.mgr !== undefined) {
      return new ReceiptPrinterWeb(this.getPrinterName(printer, true), printer);
    }

    return undefined;
  }

  private toPrinterInfo(printer: WebReceipt.ReceiptPrinterUsb): ReceiptPrinterInfo {
    return {
      name: this.getPrinterName(printer, true),
      type: this.type,
      serial: printer.printerSerial,
      uid: PrinterBase.getPrinterUid(this.type, printer.printerSerial),
      config: {
        serial: printer.printerSerial,
        width: printer.printerOptions.charactersPerLine,
      },
    };
  }

  private getPrinterName(p: WebReceipt.ReceiptPrinterUsb, short = false): string {
    return short
      ? `Receipt (#${p.printerSerial})`
      : `${p.printerManufacturer} ${p.printerModel} (S/N: ${p.printerSerial})`;
  }
}
